import { Box } from "@chakra-ui/react"
import Picker from "emoji-picker-react"
import * as React from "react"
import { FiSmile } from "react-icons/fi"
import { useClickOutside } from "../useClickOutside"
import { useRecoilValue } from "recoil"
import { brandingSelector } from "../../recoil/atoms/room"
import { Icon } from "../../components/common/icons/icon"
import styled from "@emotion/styled"
import type { RoomBranding } from "../../recoil/atoms/room/types"
import { useTranslation } from "react-i18next"
import { blendWithWhiteBackground } from "../../util"

interface StyledProps {
  roomStyles?: RoomBranding
}

const PickerContainer = styled(Box)<StyledProps>`
  .emoji-picker-react {
    border: ${({ roomStyles }) =>
      roomStyles?.servicePanelElementBorder
        ? `2px solid ${roomStyles?.servicePanelElementBorder} !important`
        : "2px solid #ced4da !important"};
    box-shadow: unset;
  }
  .emoji-search {
    background-color: ${({ roomStyles }) =>
      `${roomStyles?.servicePanelElementBackground} !important` ??
      "#e9ecef !importan"};
    color: ${({ roomStyles }) =>
      `${roomStyles.servicePanelElementPrimaryText} !important` ??
      "#495057 !importan"};
    border-color: ${({ roomStyles }) =>
      `${roomStyles?.servicePanelElementBorder} !important` ??
      "#495057 !importan"};
  }

  .emoji-categories {
    visibility: hidden;
    height: 17px;
  }

  .emoji-categories > button {
    &:hover {
      background-color: ${({ roomStyles }) =>
        `${roomStyles?.servicePanelElementBackground} !important` ??
        "#e9ecef !importan"};
    }
  }

  .emoji-group:before {
    background: ${({ roomStyles }) =>
      `${blendWithWhiteBackground(
        roomStyles?.servicePanelElementModalBackground
      )} !important` ?? "#e9ecef !important"};
    color: ${({ roomStyles }) =>
      `${roomStyles?.servicePanelElementPrimaryText} !important` ??
      "black !important"};
  }
`

export const useEmojiPicker = (
  onEmojiClick: (event: any, emojiObject: any) => void,
  callback?: (e?: any) => void,
  isSmallModal?: boolean
) => {
  const roomStyles = useRecoilValue(brandingSelector)
  const modalWrapperRef = React.useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = React.useState(false)

  const { t } = useTranslation()

  const groupNames = {
    smileys_people: t("SMILEYS & PEOPLE"),
    animals_nature: t("ANIMALS & NATURE"),
    food_drink: t("FOOD & DRINK"),
    travel_places: t("TRAVEL & PLACES"),
    activities: t("ACTIVITIES"),
    objects: t("OBJECTS"),
    symbols: t("SYMBOLS"),
    flags: t("FLAGS")
  }

  const handleOutsideClick = () => {
    if (isOpen) {
      setIsOpen(false)
    }
  }

  const toggleModal = e => {
    setIsOpen(prev => !prev)
    if (callback) {
      callback(e)
    }
  }

  useClickOutside(modalWrapperRef, handleOutsideClick)

  const handleEmojiClick = (event, emojiObject) => {
    onEmojiClick(event, emojiObject)
  }

  return {
    EmojiTrigger: ({
      isTransparent,
      styleIcon
    }: {
      isTransparent?: boolean
      styleIcon?: React.CSSProperties
    }) => (
      <Icon
        aria-label="Send emoji"
        src={FiSmile}
        color={
          isTransparent
            ? "white"
            : roomStyles?.servicePanelElementPrimaryText ?? "brand.800"
        }
        fontSize="xl"
        onClick={toggleModal}
        _focus={{}}
        style={{ ...styleIcon }}
        _hover={{ opacity: "1 !important" }}
      />
    ),
    EmojiModal: () => (
      <>
        {isOpen && (
          <PickerContainer
            ref={modalWrapperRef}
            id="chat-picker"
            roomStyles={roomStyles}
          >
            <Picker
              disableAutoFocus
              groupNames={groupNames}
              onEmojiClick={handleEmojiClick}
              disableSearchBar={isSmallModal ? true : false}
              pickerStyle={{
                width: "100%",
                position: "absolute",
                bottom: "0",
                left: "0",
                right: "0",
                zIndex: "10000001",
                height: isSmallModal && "200px",
                background:
                  blendWithWhiteBackground(
                    roomStyles?.servicePanelElementModalBackground
                  ) ?? "white",
                border: roomStyles?.servicePanelElementBorder
                  ? `2px solid ${roomStyles?.servicePanelElementBorder} !important`
                  : "2px solid #ced4da"
              }}
            />
          </PickerContainer>
        )}
      </>
    ),
    closeModal: setIsOpen
  }
}
